import React from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

const BettingProgressionCard = ({ progression = [], currentStep, eventSeedAmount }) => {
  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const getStepIcon = (step) => {
    if (step.isPast) {
      return <CheckCircle className="w-4 h-4 text-success-500" />;
    } else if (step.isCurrent) {
      return <Target className="w-4 h-4 text-primary-500 animate-pulse" />;
    } else {
      return <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600" />;
    }
  };

  const getStepColor = (step) => {
    if (step.color === 'green') {
      return 'border-success-500 bg-success-50 dark:bg-success-900/20';
    } else if (step.color === 'yellow') {
      return 'border-warning-500 bg-warning-50 dark:bg-warning-900/20';
    } else if (step.color === 'red') {
      return 'border-danger-500 bg-danger-50 dark:bg-danger-900/20';
    }
    return 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800';
  };

  const getRiskZoneTitle = (step) => {
    if (step <= 5) return '🟢 Zona Segura';
    if (step <= 9) return '🟡 Zona de Precaución';
    return '🔴 Zona de Alto Riesgo';
  };

  const groupedSteps = {
    safe: progression.filter(step => step.step <= 5),
    warning: progression.filter(step => step.step >= 6 && step.step <= 9),
    danger: progression.filter(step => step.step >= 10)
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            📈 Progresión de Apuestas
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Semilla: {formatCurrency(eventSeedAmount)} • Paso actual: {currentStep}/13
          </p>
        </div>

        <div className="text-right">
          <p className="text-sm text-gray-600 dark:text-gray-400">Próxima apuesta</p>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {formatCurrency(eventSeedAmount * Math.pow(2, currentStep - 1))}
          </p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600 dark:text-gray-400">Progreso del ciclo</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {currentStep} / 13 pasos
          </span>
        </div>

        <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-3 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(currentStep / 13) * 100}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className={`h-full rounded-full ${
              currentStep <= 5 ? 'bg-gradient-success' :
              currentStep <= 9 ? 'bg-gradient-warning' :
              'bg-gradient-danger'
            }`}
          />
        </div>

        <div className="flex justify-center mt-2">
          <span className={`text-xs px-3 py-1 rounded-full font-medium ${
            currentStep <= 5 ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300' :
            currentStep <= 9 ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300' :
            'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300'
          }`}>
            {getRiskZoneTitle(currentStep)}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {groupedSteps.safe.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-success-700 dark:text-success-300 mb-3 flex items-center">
              <div className="w-3 h-3 bg-success-500 rounded-full mr-2"></div>
              Pasos 1-5: Zona Segura
            </h4>
            <div className="grid grid-cols-5 gap-2">
              {groupedSteps.safe.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative p-3 rounded-lg border-2 transition-all duration-200 ${
                    getStepColor(step)
                  } ${
                    step.isCurrent ? 'ring-2 ring-primary-300 dark:ring-primary-700 scale-105' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-bold ${
                      step.isCurrent ? 'text-primary-700 dark:text-primary-300' :
                      step.isPast ? 'text-success-700 dark:text-success-300' :
                      'text-gray-600 dark:text-gray-400'
                    }`}>
                      #{step.step}
                    </span>
                    {getStepIcon(step)}
                  </div>

                  <div className="text-xs text-gray-700 dark:text-gray-300">
                    <div className="font-medium">
                      {formatCurrency(step.betAmount)}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Total: {formatCurrency(step.totalInvestment)}
                    </div>
                  </div>

                  {step.isCurrent && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full animate-ping"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {groupedSteps.warning.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-warning-700 dark:text-warning-300 mb-3 flex items-center">
              <div className="w-3 h-3 bg-warning-500 rounded-full mr-2"></div>
              Pasos 6-9: Zona de Precaución
            </h4>
            <div className="grid grid-cols-4 gap-2">
              {groupedSteps.warning.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: (5 + index) * 0.1 }}
                  className={`relative p-3 rounded-lg border-2 transition-all duration-200 ${
                    getStepColor(step)
                  } ${
                    step.isCurrent ? 'ring-2 ring-primary-300 dark:ring-primary-700 scale-105' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-bold ${
                      step.isCurrent ? 'text-primary-700 dark:text-primary-300' :
                      step.isPast ? 'text-success-700 dark:text-success-300' :
                      'text-gray-600 dark:text-gray-400'
                    }`}>
                      #{step.step}
                    </span>
                    {getStepIcon(step)}
                  </div>

                  <div className="text-xs text-gray-700 dark:text-gray-300">
                    <div className="font-medium">
                      {formatCurrency(step.betAmount)}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Total: {formatCurrency(step.totalInvestment)}
                    </div>
                  </div>

                  {step.isCurrent && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full animate-ping"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {groupedSteps.danger.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-danger-700 dark:text-danger-300 mb-3 flex items-center">
              <div className="w-3 h-3 bg-danger-500 rounded-full mr-2"></div>
              Pasos 10-13: Zona de Alto Riesgo
              <AlertTriangle className="w-4 h-4 ml-2 text-danger-500" />
            </h4>
            <div className="grid grid-cols-4 gap-2">
              {groupedSteps.danger.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: (9 + index) * 0.1 }}
                  className={`relative p-3 rounded-lg border-2 transition-all duration-200 ${
                    getStepColor(step)
                  } ${
                    step.isCurrent ? 'ring-2 ring-primary-300 dark:ring-primary-700 scale-105' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-bold ${
                      step.isCurrent ? 'text-primary-700 dark:text-primary-300' :
                      step.isPast ? 'text-success-700 dark:text-success-300' :
                      'text-gray-600 dark:text-gray-400'
                    }`}>
                      #{step.step}
                    </span>
                    {getStepIcon(step)}
                  </div>

                  <div className="text-xs text-gray-700 dark:text-gray-300">
                    <div className="font-medium">
                      {formatCurrency(step.betAmount)}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Total: {formatCurrency(step.totalInvestment)}
                    </div>
                  </div>

                  {step.isCurrent && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full animate-ping"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-dark-700">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Leyenda</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-success-500" />
            <span className="text-gray-600 dark:text-gray-400">Completado</span>
          </div>
          <div className="flex items-center space-x-2">
            <Target className="w-4 h-4 text-primary-500" />
            <span className="text-gray-600 dark:text-gray-400">Actual</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600" />
            <span className="text-gray-600 dark:text-gray-400">Pendiente</span>
          </div>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-4 h-4 text-danger-500" />
            <span className="text-gray-600 dark:text-gray-400">Alto Riesgo</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BettingProgressionCard;